import { useCallback, useRef, useState } from 'react'
import { usePlayer } from '@/audio/store'
import { createReorderGesture, type ReorderDrag } from './queueReorderGesture'
import { useSurfaceGesture } from './gestures/useSurfaceGesture'

interface Options {
  ids: string[]
  onMove: (from: number, to: number) => void
  onRemove: (index: number) => void
  enabled?: boolean
}

export function useDragToReorder({ ids, onMove, onRemove, enabled = true }: Options) {
  const listRef = useRef<HTMLElement | null>(null)
  const [drag, setDrag] = useState<ReorderDrag | null>(null)
  const [swipe, setSwipe] = useState<{ index: number; offset: number } | null>(null)
  const identity = usePlayer((state) => `${state.session}:${state.index}:${state.intentVersion}:${state.queue.map((item) => item.uid).join(',')}`)
  const { ref } = useSurfaceGesture((target, origin, mouse) => {
    const list = listRef.current
    const row = target.closest('[data-queue-row]')
    if (!list || !row || row.parentElement !== list || target.closest('a') || target.closest('button') !== row.querySelector('button')) return null
    const index = Array.from(list.children).indexOf(row)
    let host = list.parentElement
    while (host && !/(auto|scroll)/.test(getComputedStyle(host).overflowY)) host = host.parentElement
    const scroller = host
    const reorder = createReorderGesture({ count: ids.length,
      geometry: () => { const box = scroller?.getBoundingClientRect(); return {
        // Reordering moves by the full row pitch, including the space between cards.
        rowHeight: (row.getBoundingClientRect().height || 64) + (parseFloat(getComputedStyle(list).rowGap) || 0), scrollTop: scroller?.scrollTop ?? window.scrollY,
        top: Math.max(0, box?.top ?? 0), bottom: Math.min(window.innerHeight, box?.bottom ?? window.innerHeight),
      } },
      scrollBy: (distance) => { if (scroller) scroller.scrollTop += distance; else window.scrollBy(0, distance) },
      onActivate() {}, onDrag: setDrag, onCommit: onMove,
    })
    return {
      directions: mouse ? [] : ['left', 'right'],
      hold: ids.length > 1 ? () => reorder.lift(index, origin) : undefined,
      onActivate() {},
      onMove: (mode, point, distance) => {
        if (mode === 'hold') reorder.move(point)
        else setSwipe({ index, offset: (mode === 'left' ? -1 : 1) * Math.min(112, distance) })
      },
      onCommit: (mode) => { if (mode === 'hold') reorder.drop(); else if (mode === 'left') onRemove(index) },
      onReset: () => { reorder.cancel(); setSwipe(null) },
    }
  }, identity, enabled)
  function restingOffset(index: number) {
    if (!drag || index === drag.activeIndex) return 0
    if (drag.activeIndex < drag.overIndex && index > drag.activeIndex && index <= drag.overIndex) return -drag.rowHeight
    if (drag.activeIndex > drag.overIndex && index < drag.activeIndex && index >= drag.overIndex) return drag.rowHeight
    return 0
  }
  const bindList = useCallback((node: HTMLUListElement | null) => { listRef.current = node; ref(node) }, [ref])
  return { listRef, bindList, drag, swipe, restingOffset }
}
